import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, Pagination, TextField } from "@mui/material";
import { Link, useParams } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const DEFAULT_LIMIT = 20;

const formatMarks = (value) => {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(2);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (String(value).startsWith("http")) {
    return value;
  }

  return `${Constants.imageDomain}${value}`;
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "AQ";
  }

  return parts
    .slice(0, 2)
    .map((item) => item[0]?.toUpperCase() || "")
    .join("");
};

const StatPill = ({ label, value, accent }) => (
  <MDBox
    px={2}
    py={1.5}
    borderRadius="xl"
    sx={{
      backgroundColor: accent,
      minWidth: 118,
    }}
  >
    <MDTypography variant="caption" color="white" sx={{ opacity: 0.82 }}>
      {label}
    </MDTypography>
    <MDTypography variant="h6" color="white" fontWeight="bold">
      {value}
    </MDTypography>
  </MDBox>
);

export default function TestSeriesLeaderboard() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: DEFAULT_LIMIT,
  });
  const [topper, setTopper] = useState(null);
  const [summary, setSummary] = useState({
    total_participants: 0,
    submitted_participants: 0,
  });
  const [testseries, setTestseries] = useState(null);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("error");

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);

      if (init() !== "success") {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${Constants.getUrls.getTestSeriesLeaderboard}${id}`, {
          params: {
            page,
            limit,
          },
        });

        setLeaderboard(response?.data?.data?.docs || []);
        setPagination({
          page: response?.data?.data?.page || page,
          totalPages: response?.data?.data?.totalPages || 1,
          totalDocs: response?.data?.data?.totalDocs || 0,
          limit: response?.data?.data?.limit || limit,
        });
        setTopper(response?.data?.topper || null);
        setSummary(
          response?.data?.summary || {
            total_participants: 0,
            submitted_participants: 0,
          }
        );
        setTestseries(response?.data?.testseries || null);
      } catch (error) {
        setNotification(true);
        setNotiColor("error");
        setMessage(error?.response?.data?.message || "Unable to load leaderboard.");
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [id, page, limit]);

  const topperName = topper?.user?.name || "No topper yet";
  const topperCollege =
    topper?.user?.university?.short_name || topper?.user?.university?.name || "College not available";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ overflow: "hidden" }}>
          <MDBox
            px={3}
            py={3}
            sx={{
              background:
                "linear-gradient(135deg, rgba(14,43,30,1) 0%, rgba(24,73,44,1) 55%, rgba(242,117,0,0.95) 100%)",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <MDBox>
                <MDTypography variant="button" color="white" sx={{ opacity: 0.78 }}>
                  Test Series Leaderboard
                </MDTypography>
                <MDTypography variant="h4" color="white" fontWeight="bold">
                  {testseries?.test_series_name || "Loading leaderboard"}
                </MDTypography>
                <MDTypography variant="button" color="white" sx={{ opacity: 0.82 }}>
                  {summary.submitted_participants} submitted out of {summary.total_participants} participants
                </MDTypography>
              </MDBox>

              <Link to="/testseries">
                <MDButton variant="contained" color="warning">
                  Back to Test Series
                </MDButton>
              </Link>
            </MDBox>
          </MDBox>

          <MDBox p={3}>
            {loading ? (
              <Grid container justifyContent="center">
                <MDTypography variant="h6">Loading leaderboard...</MDTypography>
              </Grid>
            ) : (
              <>
                {topper ? (
                  <Card
                    sx={{
                      mb: 3,
                      border: "1px solid rgba(24,53,38,0.08)",
                      boxShadow: "0px 24px 60px rgba(18, 43, 29, 0.08)",
                    }}
                  >
                    <MDBox p={3}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <MDBox display="flex" alignItems="center" gap={2}>
                            {topper?.user?.profile_pic_url ? (
                              <MDBox
                                component="img"
                                src={buildImageUrl(topper.user.profile_pic_url)}
                                alt={topperName}
                                sx={{
                                  width: 92,
                                  height: 92,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "4px solid rgba(242,117,0,0.18)",
                                }}
                              />
                            ) : (
                              <MDBox
                                width="92px"
                                height="92px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="50%"
                                sx={{
                                  background:
                                    "linear-gradient(135deg, rgba(17,68,44,1) 0%, rgba(242,117,0,0.96) 100%)",
                                }}
                              >
                                <MDTypography variant="h3" color="white">
                                  {getInitials(topperName)}
                                </MDTypography>
                              </MDBox>
                            )}

                            <MDBox>
                              <MDTypography variant="button" color="warning" fontWeight="bold">
                                Rank #{topper.rank}
                              </MDTypography>
                              <MDTypography variant="h4" fontWeight="bold">
                                {topperName}
                              </MDTypography>
                              <MDTypography variant="button" color="text">
                                {topperCollege}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <MDBox
                            display="flex"
                            flexWrap="wrap"
                            gap={1.5}
                            justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          >
                            <StatPill
                              label="Marks Obtained"
                              value={formatMarks(topper.marks_obtained)}
                              accent="#183526"
                            />
                            <StatPill
                              label="Correct"
                              value={topper.correct_count}
                              accent="#21633d"
                            />
                            <StatPill
                              label="Incorrect"
                              value={topper.incorrect_count}
                              accent="#8f2d1f"
                            />
                            <StatPill
                              label="Skipped"
                              value={topper.skipped_count}
                              accent="#6b7280"
                            />
                          </MDBox>
                          <MDBox
                            mt={2}
                            display="flex"
                            flexWrap="wrap"
                            gap={2}
                            justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          >
                            <MDTypography variant="button" color="text">
                              Accuracy: {Number(topper.accuracy_percent || 0).toFixed(0)}%
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                              Total Questions: {topper.total_questions || 0}
                            </MDTypography>
                            <MDTypography variant="button" color="text">
                              Submitted: {formatDate(topper.submitted_at)}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                ) : null}

                <Grid container spacing={2}>
                  {leaderboard.length ? (
                    leaderboard.map((item) => {
                      const participantName = item?.user?.name || "Unknown User";
                      const participantCollege =
                        item?.user?.university?.short_name ||
                        item?.user?.university?.name ||
                        "College not available";

                      return (
                        <Grid item xs={12} key={item._id}>
                          <Card
                            sx={{
                              border: "1px solid rgba(24,53,38,0.08)",
                              boxShadow: "0px 14px 34px rgba(18, 43, 29, 0.06)",
                            }}
                          >
                            <MDBox p={2.5}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                  <MDBox display="flex" alignItems="center" gap={2}>
                                    <MDBox
                                      width="54px"
                                      height="54px"
                                      borderRadius="xl"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        backgroundColor: "#183526",
                                      }}
                                    >
                                      <MDTypography variant="h6" color="white" fontWeight="bold">
                                        #{item.rank}
                                      </MDTypography>
                                    </MDBox>

                                    <MDBox>
                                      <MDTypography variant="h6" fontWeight="bold">
                                        {participantName}
                                      </MDTypography>
                                      <MDTypography variant="button" color="text">
                                        {participantCollege}
                                      </MDTypography>
                                      <MDTypography variant="caption" display="block" color="text">
                                        {item?.user?.mobile || item?.user?.email || "-"}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                  <Grid container spacing={1.5}>
                                    <Grid item xs={6} md={3}>
                                      <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                                        <MDTypography variant="caption" color="text">
                                          Marks
                                        </MDTypography>
                                        <MDTypography variant="h6" fontWeight="bold">
                                          {formatMarks(item.marks_obtained)}
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <MDBox p={1.5} borderRadius="lg" bgColor="success">
                                        <MDTypography variant="caption" color="white">
                                          Correct
                                        </MDTypography>
                                        <MDTypography variant="h6" color="white" fontWeight="bold">
                                          {item.correct_count}
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <MDBox p={1.5} borderRadius="lg" bgColor="error">
                                        <MDTypography variant="caption" color="white">
                                          Incorrect
                                        </MDTypography>
                                        <MDTypography variant="h6" color="white" fontWeight="bold">
                                          {item.incorrect_count}
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                      <MDBox p={1.5} borderRadius="lg" bgColor="secondary">
                                        <MDTypography variant="caption" color="white">
                                          Accuracy
                                        </MDTypography>
                                        <MDTypography variant="h6" color="white" fontWeight="bold">
                                          {Number(item.accuracy_percent || 0).toFixed(0)}%
                                        </MDTypography>
                                      </MDBox>
                                    </Grid>
                                  </Grid>
                                  <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                                    Submitted on {formatDate(item.submitted_at)}
                                  </MDTypography>
                                </Grid>
                              </Grid>
                            </MDBox>
                          </Card>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6">No submitted attempts found yet.</MDTypography>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                <MDBox
                  mt={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <MDBox display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                    <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                      Rows per page
                    </MDTypography>
                    <TextField
                      select
                      size="small"
                      value={limit}
                      onChange={(event) => {
                        setLimit(Number(event.target.value));
                        setPage(1);
                      }}
                      sx={{ width: 82 }}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </TextField>
                    <MDTypography variant="caption" sx={{ color: "#d7e5ff" }}>
                      {pagination.totalDocs
                        ? `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(
                            pagination.page * pagination.limit,
                            pagination.totalDocs
                          )} of ${pagination.totalDocs}`
                        : "0 records"}
                    </MDTypography>
                  </MDBox>
                  {pagination.totalPages > 1 ? (
                    <Pagination
                      page={pagination.page}
                      count={pagination.totalPages}
                      variant="outlined"
                      shape="rounded"
                      sx={agriPaginationSx}
                      onChange={(event, nextPage) => setPage(nextPage)}
                    />
                  ) : null}
                </MDBox>
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="Agriquiz"
        content={message}
        dateTime="Just now"
        open={notification}
      />
    </DashboardLayout>
  );
}
